import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/context/tenant-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { RefreshCw, Zap, Play, Pause, Bot, Clock, CheckCircle, XCircle, Hash, Mail, FileText, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";

function cronToText(cron: string | null): string {
  if (!cron) return "Manual";
  const presets: Record<string, string> = {
    "0 * * * *": "Every hour",
    "0 9 * * *": "Daily at 09:00",
    "0 9 * * 1-5": "Weekdays at 09:00",
    "0 9 * * 1": "Weekly (Mon 09:00)",
    "0 9 1 * *": "Monthly (1st at 09:00)",
    "*/15 * * * *": "Every 15 min",
    "*/30 * * * *": "Every 30 min",
    "0 */2 * * *": "Every 2 hours",
    "0 */4 * * *": "Every 4 hours",
    "0 0 * * *": "Daily at midnight",
  };
  // Normalize: parse the cron, handle object format {value: "..."} from some APIs
  const normalized = typeof cron === "object" ? (cron as unknown as { value: string }).value ?? String(cron) : cron;
  return presets[normalized] ?? normalized;
}

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "Never";
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  if (diff < 60_000) return "Just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

/** Describe where a task's output will land. */
function describeDestination(task: AgentTask): { icon: typeof Hash; label: string } {
  if (task.announceChannelName) {
    return { icon: Hash, label: task.announceChannelName };
  }
  const prompt = (task.taskPrompt || "").toLowerCase();
  if (prompt.includes("email") || prompt.includes(" mail ") || prompt.includes("send to")) {
    return { icon: Mail, label: "via agent email" };
  }
  return { icon: FileText, label: "Task Reports" };
}

export function TasksSurface() {
  const { tenantId } = useTenant();
  const { threadContext } = useWorkspace();
  const queryClient = useQueryClient();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const selectedAgent = agents.find((a) => a.id === threadContext.agentId);
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ["agent-tasks", tenantId, threadContext.agentId],
    queryFn: () => agentTasksApi.list(tenantId, { agentId: threadContext.agentId }),
    enabled: !!tenantId && !!threadContext.agentId,
  });

  const triggerTask = useMutation({
    mutationFn: (taskId: string) => agentTasksApi.trigger(taskId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-tasks", tenantId, threadContext.agentId] }),
  });

  const toggleTask = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => agentTasksApi.update(id, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agent-tasks", tenantId, threadContext.agentId] }),
  });

  if (!threadContext.agentId) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Bot size={40} className="mx-auto mb-3 text-text-muted" />
          <p className="text-sm text-text-secondary">Select an agent to view their tasks</p>
        </div>
      </div>
    );
  }

  const recurring = tasks.filter((t) => t.type === "recurring");
  const oneOff = tasks.filter((t) => t.type === "one_off");

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="min-h-14 shrink-0 border-b border-border bg-surface px-6 py-2 lg:px-10">
        <div className="mx-auto max-w-4xl flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-text-primary">
              {selectedAgent?.name ?? "Agent"} Tasks
            </h2>
            <p className="text-xs text-text-muted">
              {recurring.length} recurring · {oneOff.length} one-off
            </p>
          </div>
        </div>
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto chat-scroll px-6 py-4 lg:px-10">
        <div className="mx-auto max-w-4xl">
          {isLoading ? (
            <p className="text-sm text-text-muted py-8 text-center">Loading tasks...</p>
          ) : tasks.length === 0 ? (
            <div className="py-12 text-center">
              <Clock size={40} className="mx-auto mb-3 text-text-muted" />
              <p className="text-sm text-text-secondary mb-1">No tasks configured</p>
              <p className="text-xs text-text-muted">
                This agent has no scheduled or one-off tasks. Tasks can be created from the agent detail page.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Recurring tasks */}
              {recurring.length > 0 && (
                <>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted mb-1 mt-2">
                    Recurring
                  </p>
                  {recurring.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onTrigger={() => triggerTask.mutate(task.id)}
                      onToggle={() => toggleTask.mutate({ id: task.id, enabled: !task.enabled })}
                      onOpen={() => setSelectedTask(task)}
                      isTriggering={triggerTask.isPending}
                    />
                  ))}
                </>
              )}

              {/* One-off tasks */}
              {oneOff.length > 0 && (
                <>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-text-muted mb-1 mt-4">
                    One-Off
                  </p>
                  {oneOff.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onTrigger={() => triggerTask.mutate(task.id)}
                      onToggle={() => toggleTask.mutate({ id: task.id, enabled: !task.enabled })}
                      onOpen={() => setSelectedTask(task)}
                      isTriggering={triggerTask.isPending}
                    />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail / edit drawer — shared with the /tasks page */}
      <TaskDetailDrawer
        task={selectedTask}
        tenantId={tenantId}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  );
}

function TaskCard({
  task,
  onTrigger,
  onToggle,
  onOpen,
  isTriggering,
}: {
  task: AgentTask;
  onTrigger: () => void;
  onToggle: () => void;
  onOpen: () => void;
  isTriggering: boolean;
}) {
  const isRecurring = task.type === "recurring";
  const lastRunOk = task.lastResult?.success !== false;
  const dest = describeDestination(task);
  const DestIcon = dest.icon;

  return (
    <div
      onClick={onOpen}
      className="cursor-pointer rounded-xl border border-border bg-background px-4 py-3 transition-colors hover:border-accent/30 hover:bg-surface-hover"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg mt-0.5",
            isRecurring ? "bg-blue-500/10" : "bg-amber-500/10",
          )}>
            {isRecurring ? (
              <RefreshCw size={15} className="text-blue-500" />
            ) : (
              <Zap size={15} className="text-amber-500" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">{task.title}</p>
            {task.description && (
              <p className="text-xs text-text-muted mt-0.5 line-clamp-1">{task.description}</p>
            )}
            <div className="flex items-center gap-3 mt-1.5 flex-wrap">
              {isRecurring && (
                <span className="text-[11px] text-text-secondary flex items-center gap-1">
                  <Clock size={10} />
                  {cronToText(task.cronExpression)}
                </span>
              )}
              {task.lastRunAt && (
                <span className="text-[11px] text-text-muted flex items-center gap-1">
                  {lastRunOk ? (
                    <CheckCircle size={10} className="text-green-500" />
                  ) : (
                    <XCircle size={10} className="text-red-500" />
                  )}
                  Last: {relativeTime(task.lastRunAt)}
                </span>
              )}
              {task.nextRunAt && task.enabled && (
                <span className="text-[11px] text-text-muted">
                  Next: {relativeTime(task.nextRunAt)}
                </span>
              )}
              <span className="text-[11px] text-text-muted flex items-center gap-1">
                <DestIcon size={10} />
                {dest.label}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Badge
            variant={task.enabled ? "success" : "secondary"}
            className="text-[10px]"
          >
            {task.enabled ? "Active" : "Paused"}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={(e) => { e.stopPropagation(); onTrigger(); }}
            disabled={isTriggering}
            title="Run now"
          >
            <Play size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title={task.enabled ? "Pause" : "Resume"}
          >
            <Pause size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            title="Edit task"
          >
            <Pencil size={13} />
          </Button>
        </div>
      </div>
    </div>
  );
}
